import Vue from "vue";
import Vuex from "vuex";
import axios from "axios";
import { fetchAllAudiencePackages } from "@/api/audience";

Vue.use(Vuex);

//与业务逻辑无关，仅做mock数据使用
const customerCatalog = [
  {
    id: "dongfeng_nissan",
    name: "东风日产",
    shortName: "日产",
    theme: "#d73a31",
  },
  { id: "changan_auto", name: "长安汽车", shortName: "长安", theme: "#0f6bdc" },
  { id: "bmw", name: "宝马", shortName: "BMW", theme: "#1c69d4" },
  { id: "gac_trumpchi", name: "传祺", shortName: "传祺", theme: "#7f1d1d" },
  { id: "mercedes_benz", name: "奔驰", shortName: "奔驰", theme: "#111111" },
  {
    id: "lexus_auto",
    name: "雷克萨斯汽车",
    shortName: "雷克萨斯",
    theme: "#5b6475",
  },
  { id: "li_auto", name: "理想汽车", shortName: "理想", theme: "#00b96b" },
  { id: "taobao_union", name: "淘宝联盟", shortName: "淘宝", theme: "#ff6a00" },
  { id: "pinduoduo", name: "拼多多", shortName: "拼多多", theme: "#e02020" },
];

function getInitialCustomerId() {
  if (typeof window === "undefined") {
    return customerCatalog[0].id;
  }
  return localStorage.getItem("activeCustomerId") || customerCatalog[0].id;
}

function shouldUseCustomerCache(state, cacheKey) {
  const cache = state.cacheMeta[cacheKey];
  return !!(
    cache &&
    cache.loadedAt &&
    cache.customerId === state.activeCustomerId
  );
}

export default new Vuex.Store({
  state: {
    campaigns: [],
    campaignActivities: [],
    materials: [],
    interestTags: [],
    appList: [],
    audiencePackages: [],
    mediaSlots: [],
    reports: {
      rows: [],
      summary: {
        impressions: "0",
        clicks: "0",
        conversions: "0",
        spend: "¥0",
        ctr: "0.00%",
        cvr: "0.00%",
      },
      trend: {
        days: [],
      },
      periods: {},
    },
    customers: customerCatalog,
    activeCustomerId: getInitialCustomerId(),
    cacheMeta: {
      campaigns: { customerId: "", loadedAt: 0 },
      campaignActivities: { customerId: "", loadedAt: 0 },
      materials: { customerId: "", loadedAt: 0 },
      audiencePackages: { customerId: "", loadedAt: 0 },
      mediaSlots: { customerId: "", loadedAt: 0 },
      reports: { customerId: "", loadedAt: 0 },
    },
    loading: false,
    error: null,
  },
  mutations: {
    SET_CAMPAIGNS(state, campaigns) {
      state.campaigns = campaigns;
    },
    SET_CAMPAIGN_ACTIVITIES(state, activities) {
      state.campaignActivities = activities;
    },
    SET_MATERIALS(state, materials) {
      state.materials = materials;
    },
    SET_INTEREST_TAGS(state, tags) {
      state.interestTags = tags;
    },
    SET_APP_LIST(state, apps) {
      state.appList = apps;
    },
    SET_AUDIENCE_PACKAGES(state, packages) {
      state.audiencePackages = packages;
    },
    SET_MEDIA_SLOTS(state, mediaSlots) {
      state.mediaSlots = mediaSlots;
    },
    SET_REPORTS(state, reports) {
      state.reports = reports;
    },
    SET_ACTIVE_CUSTOMER(state, customerId) {
      state.activeCustomerId = customerId;
      if (typeof window !== "undefined") {
        localStorage.setItem("activeCustomerId", customerId);
      }
    },
    SET_CACHE_META(state, { key, customerId, loadedAt }) {
      if (!state.cacheMeta[key]) {
        return;
      }
      state.cacheMeta[key] = {
        customerId,
        loadedAt,
      };
    },
    INVALIDATE_CACHE(state, keys) {
      const targetKeys = Array.isArray(keys) ? keys : [keys];
      targetKeys.forEach((key) => {
        if (!state.cacheMeta[key]) {
          return;
        }
        state.cacheMeta[key] = {
          customerId: "",
          loadedAt: 0,
        };
      });
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    ADD_CAMPAIGN(state, campaign) {
      state.campaigns.push(campaign);
    },
    UPDATE_CAMPAIGN(state, updatedCampaign) {
      const index = state.campaigns.findIndex(
        (c) => c.id === updatedCampaign.id,
      );
      if (index !== -1) {
        state.campaigns.splice(index, 1, updatedCampaign);
      }
    },
    REMOVE_CAMPAIGN(state, campaignId) {
      state.campaigns = state.campaigns.filter((c) => c.id !== campaignId);
    },
    ADD_MATERIAL(state, material) {
      const index = state.materials.findIndex(
        (item) => item.id === material.id,
      );
      if (index !== -1) {
        state.materials.splice(index, 1, material);
      } else {
        state.materials.push(material);
      }
    },
    ADD_MEDIA_SLOT(state, mediaSlot) {
      state.mediaSlots.push(mediaSlot);
    },
    UPDATE_MEDIA_SLOT(state, updatedMediaSlot) {
      const index = state.mediaSlots.findIndex(
        (m) => m.id === updatedMediaSlot.id,
      );
      if (index !== -1) {
        state.mediaSlots.splice(index, 1, updatedMediaSlot);
      }
    },
    REMOVE_MEDIA_SLOT(state, mediaSlotId) {
      state.mediaSlots = state.mediaSlots.filter((m) => m.id !== mediaSlotId);
    },
  },
  actions: {
    invalidateCache({ commit }, keys) {
      commit("INVALIDATE_CACHE", keys);
    },
    setActiveCustomer({ commit, state }, customerId) {
      const exists = state.customers.some(
        (customer) => customer.id === customerId,
      );
      if (!exists) {
        return;
      }
      commit("SET_ACTIVE_CUSTOMER", customerId);
    },
    async refreshCustomerData({ dispatch }) {
      dispatch("invalidateCache", ["campaigns", "campaignActivities", "materials", "audiencePackages", "mediaSlots", "reports"]);
      await Promise.all([
        dispatch("fetchCampaignActivities", { force: true }),
        dispatch("fetchCampaigns", { force: true }),
        dispatch("fetchMaterials", { force: true }),
        dispatch("fetchAudiencePackages", { force: true }),
        dispatch("fetchMediaSlots", { force: true }),
        dispatch("fetchReports", { force: true }),
      ]);
    },
    async fetchCampaigns({ commit, state }, options = {}) {
      if (!options.force && shouldUseCustomerCache(state, "campaigns")) {
        return state.campaigns;
      }
      commit("SET_LOADING", true);
      try {
        const response = await axios.get("/campaigns");
        if (response.data.code === 0) {
          commit("SET_CAMPAIGNS", response.data.data);
          commit("SET_CACHE_META", {
            key: "campaigns",
            customerId: state.activeCustomerId,
            loadedAt: Date.now(),
          });
          return response.data.data;
        } else {
          commit("SET_ERROR", response.data.message);
        }
      } catch (error) {
        commit("SET_ERROR", error.message);
      } finally {
        commit("SET_LOADING", false);
      }
    },
    async fetchCampaignActivities({ commit, state }, options = {}) {
      if (!options.force && shouldUseCustomerCache(state, "campaignActivities")) {
        return state.campaignActivities;
      }
      commit("SET_LOADING", true);
      try {
        const response = await axios.get("/campaign_activities");
        if (response.data.code === 0) {
          commit("SET_CAMPAIGN_ACTIVITIES", response.data.data);
          commit("SET_CACHE_META", {
            key: "campaignActivities",
            customerId: state.activeCustomerId,
            loadedAt: Date.now(),
          });
          return response.data.data;
        } else {
          commit("SET_ERROR", response.data.message);
        }
      } catch (error) {
        commit("SET_ERROR", error.message);
      } finally {
        commit("SET_LOADING", false);
      }
    },
    async fetchMaterials({ commit, state }, options = {}) {
      if (!options.force && shouldUseCustomerCache(state, "materials")) {
        return state.materials;
      }
      commit("SET_LOADING", true);
      try {
        const response = await axios.get("/materials");
        if (response.data.code === 0) {
          commit("SET_MATERIALS", response.data.data);
          commit("SET_CACHE_META", {
            key: "materials",
            customerId: state.activeCustomerId,
            loadedAt: Date.now(),
          });
          return response.data.data;
        } else {
          commit("SET_ERROR", response.data.message);
        }
      } catch (error) {
        commit("SET_ERROR", error.message);
      } finally {
        commit("SET_LOADING", false);
      }
    },
    async createCampaign({ commit }, campaignData) {
      commit("SET_LOADING", true);
      try {
        const response = await axios.post("/campaigns", campaignData);
        if (response.data.code === 0) {
          const createdId =
            (response.data && response.data.data && response.data.data.id) ||
            campaignData.id ||
            null;
          const createdCampaign = {
            ...campaignData,
            ...(createdId ? { id: createdId } : {}),
          };
          try {
            commit("ADD_CAMPAIGN", createdCampaign);
            commit("INVALIDATE_CACHE", ["campaigns", "reports"]);
          } catch (syncError) {
            console.error("Campaign created but local state sync failed:", syncError);
          }
          return {
            id: createdId,
          };
        } else {
          commit("SET_ERROR", response.data.message);
          return null;
        }
      } catch (error) {
        commit("SET_ERROR", error.message);
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },
    async updateCampaign({ commit }, { id, data }) {
      commit("SET_LOADING", true);
      try {
        const response = await axios.put(`/campaigns/${id}`, data);
        if (response.data.code === 0) {
          data.id = id;
          commit("UPDATE_CAMPAIGN", data);
          commit("INVALIDATE_CACHE", ["campaigns", "reports"]);
          return true;
        } else {
          commit("SET_ERROR", response.data.message);
          return false;
        }
      } catch (error) {
        commit("SET_ERROR", error.message);
        return false;
      } finally {
        commit("SET_LOADING", false);
      }
    },
    async deleteCampaign({ commit, dispatch }, campaignId) {
      commit("SET_LOADING", true);
      try {
        const response = await axios.delete(`/campaigns/${campaignId}`);
        if (response.data.code === 0) {
          commit("REMOVE_CAMPAIGN", campaignId);
          commit("INVALIDATE_CACHE", ["campaigns", "reports"]);
          await dispatch("fetchReports", { force: true });
          return true;
        } else {
          commit("SET_ERROR", response.data.message);
          return false;
        }
      } catch (error) {
        commit("SET_ERROR", error.message);
        return false;
      } finally {
        commit("SET_LOADING", false);
      }
    },
    async uploadMaterial(
      { commit, dispatch },
      { file, type, activityId, activityName, relativePath, batchMode = false },
    ) {
      if (!batchMode) {
        commit("SET_LOADING", true);
      }
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("type", type);
        if (activityId) {
          formData.append("activity_id", activityId);
        }
        if (activityName) {
          formData.append("activity_name", activityName);
        }
        if (relativePath) {
          formData.append("relative_path", relativePath);
        }

        const response = await axios.post("/materials/upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (response.data.code === 0) {
          commit("INVALIDATE_CACHE", ["materials", "audiencePackages", "campaignActivities", "reports"]);
          if (!batchMode) {
            if (type === "audience") {
              await dispatch("fetchAudiencePackages", { force: true });
            } else {
              commit("ADD_MATERIAL", response.data.data);
              await dispatch("fetchCampaignActivities", { force: true });
            }
          }
          return response.data.data;
        } else {
          commit("SET_ERROR", response.data.message);
          return null;
        }
      } catch (error) {
        commit("SET_ERROR", error.message);
        return null;
      } finally {
        if (!batchMode) {
          commit("SET_LOADING", false);
        }
      }
    },
    async fetchInterestTags({ commit }) {
      commit("SET_LOADING", true);
      try {
        const response = await axios.get("/interest_tags");
        if (response.data.code === 0) {
          commit("SET_INTEREST_TAGS", response.data.data);
        } else {
          commit("SET_ERROR", response.data.message);
        }
      } catch (error) {
        commit("SET_ERROR", error.message);
      } finally {
        commit("SET_LOADING", false);
      }
    },
    async fetchAppList({ commit }) {
      commit("SET_LOADING", true);
      try {
        const response = await axios.get("/app_list");
        if (response.data.code === 0) {
          commit("SET_APP_LIST", response.data.data);
        } else {
          commit("SET_ERROR", response.data.message);
        }
      } catch (error) {
        commit("SET_ERROR", error.message);
      } finally {
        commit("SET_LOADING", false);
      }
    },
    async fetchAudiencePackages({ commit, state }, options = {}) {
      if (!options.force && shouldUseCustomerCache(state, "audiencePackages")) {
        return state.audiencePackages;
      }
      commit("SET_LOADING", true);
      try {
        const packages = await fetchAllAudiencePackages();
        commit("SET_AUDIENCE_PACKAGES", packages);
        commit("SET_CACHE_META", {
          key: "audiencePackages",
          customerId: state.activeCustomerId,
          loadedAt: Date.now(),
        });
        return packages;
      } catch (error) {
        commit("SET_ERROR", error.message);
      } finally {
        commit("SET_LOADING", false);
      }
    },
    async fetchMediaSlots({ commit, state }, options = {}) {
      if (!options.force && shouldUseCustomerCache(state, "mediaSlots")) {
        return state.mediaSlots;
      }
      commit("SET_LOADING", true);
      try {
        const response = await axios.get("/media_slots");
        if (response.data.code === 0) {
          commit("SET_MEDIA_SLOTS", response.data.data);
          commit("SET_CACHE_META", {
            key: "mediaSlots",
            customerId: state.activeCustomerId,
            loadedAt: Date.now(),
          });
          return response.data.data;
        } else {
          commit("SET_ERROR", response.data.message);
        }
      } catch (error) {
        commit("SET_ERROR", error.message);
      } finally {
        commit("SET_LOADING", false);
      }
    },
    async fetchReports({ commit, state }, options = {}) {
      if (!options.force && shouldUseCustomerCache(state, "reports")) {
        return state.reports;
      }
      commit("SET_LOADING", true);
      try {
        const response = await axios.get("/reports");
        if (response.data.code === 0) {
          commit("SET_REPORTS", response.data.data);
          commit("SET_CACHE_META", {
            key: "reports",
            customerId: state.activeCustomerId,
            loadedAt: Date.now(),
          });
          return response.data.data;
        } else {
          commit("SET_ERROR", response.data.message);
        }
      } catch (error) {
        commit("SET_ERROR", error.message);
      } finally {
        commit("SET_LOADING", false);
      }
    },
    async createMediaSlot({ commit }, mediaSlotData) {
      commit("SET_LOADING", true);
      try {
        const response = await axios.post("/media_slots", mediaSlotData);
        if (response.data.code === 0) {
          commit("ADD_MEDIA_SLOT", mediaSlotData);
          commit("INVALIDATE_CACHE", "mediaSlots");
          return mediaSlotData.id;
        } else {
          commit("SET_ERROR", response.data.message);
          return null;
        }
      } catch (error) {
        commit("SET_ERROR", error.message);
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },
    async updateMediaSlot({ commit }, { id, data }) {
      commit("SET_LOADING", true);
      try {
        const response = await axios.put(`/media_slots/${id}`, data);
        if (response.data.code === 0) {
          commit("UPDATE_MEDIA_SLOT", { ...data, id });
          commit("INVALIDATE_CACHE", "mediaSlots");
          return true;
        } else {
          commit("SET_ERROR", response.data.message);
          return false;
        }
      } catch (error) {
        commit("SET_ERROR", error.message);
        return false;
      } finally {
        commit("SET_LOADING", false);
      }
    },
    async deleteMediaSlot({ commit }, mediaSlotId) {
      commit("SET_LOADING", true);
      try {
        const response = await axios.delete(`/media_slots/${mediaSlotId}`);
        if (response.data.code === 0) {
          commit("REMOVE_MEDIA_SLOT", mediaSlotId);
          commit("INVALIDATE_CACHE", "mediaSlots");
          return true;
        } else {
          commit("SET_ERROR", response.data.message);
          return false;
        }
      } catch (error) {
        commit("SET_ERROR", error.message);
        return false;
      } finally {
        commit("SET_LOADING", false);
      }
    },
  },
  getters: {
    activeCustomer: (state) => {
      return (
        state.customers.find(
          (customer) => customer.id === state.activeCustomerId,
        ) || state.customers[0]
      );
    },
    getCampaignById: (state) => (id) => {
      return state.campaigns.find((campaign) => campaign.id === id);
    },
    getMaterialById: (state) => (id) => {
      return state.materials.find((material) => material.id === id);
    },
    getActiveCampaigns: (state) => {
      return state.campaigns.filter((campaign) => campaign.status === "active");
    },
    getCampaignActivityById: (state) => (id) => {
      return state.campaignActivities.find((activity) => activity.id === id);
    },
    materialsByType: (state) => (type) => {
      return state.materials.filter((material) => material.type === type);
    },
    getMediaSlotById: (state) => (id) => {
      return state.mediaSlots.find((mediaSlot) => mediaSlot.id === id);
    },
    getActiveMediaSlots: (state) => {
      return state.mediaSlots.filter(
        (mediaSlot) => mediaSlot.status === "active",
      );
    },
    mediaSlotsByPlatform: (state) => (platform) => {
      return state.mediaSlots.filter(
        (mediaSlot) => mediaSlot.platform === platform,
      );
    },
    mediaSlotsByAdType: (state) => (adType) => {
      return state.mediaSlots.filter(
        (mediaSlot) => mediaSlot.adType === adType,
      );
    },
  },
});
